import Decimal from 'decimal.js'
import {
  convertDurationToYears,
  CustomCorrosionRegion,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

export function calculateCorrosionMultiplier(
  region: CustomCorrosionRegion,
  binRectFeet: {
    x: { start: GeckoMeasurement; end: GeckoMeasurement }
    y: { start: GeckoMeasurement; end: GeckoMeasurement }
  },
  thickness: GeckoMeasurement,
  rca: Decimal | null,
  lossBinPerYear: Decimal
) {
  let corrosionMultiplier

  // We're comparing end to start (and vice versa) to handle the case
  // where bins are half-in half-out of the region
  // TODO: This should probably be an overlap check
  if (
    binRectFeet.x.end.num > region.xStart &&
    binRectFeet.x.start.num < region.xEnd &&
    binRectFeet.y.end.num > region.yStart &&
    binRectFeet.y.start.num < region.yEnd
  ) {
    /**
     * Calculating the custom corrosion rate multiplier here
     * If we have a duration for the custom corrosion rate, we calculate a combined corrosion rate
     * The combined corrosion rate is a weighted average of the custom corrosion rate and the regular corrosion rate
     *
     * The formula for that is:
     * CRc = CRo * (CRnew * d + (Tnew - Tmin)) / (Tnew - Tmin + (d * CRo))
     * Where:
     * - CRc is the combined corrosion rate
     * - CRo is the original corrosion rate
     * - CRnew is the new corrosion rate
     * - d is the duration
     * - Tnew is the new thickness after the liner duration
     * - Tmin is the minimum thickness
     *
     * If the new thickness is less than Tmin, we just use the new corrosion rate.
     * Intuitively, this is because if we hit Tmin when still in the liner duration,
     * we should only be using the new corrosion rate in the RL calc.
     *
     * In the code below, we compute a multiplier, which multiplies CRo in the formula.
     *
     * For further explanation of the formula, see this Desmos notebook:
     * https://www.desmos.com/calculator/3asjhe0q1i
     */
    const duration = convertDurationToYears(region.customCorrosionRateDuration)

    // If we have a duration, calculate a combined corrosion rate
    if (rca !== null && duration) {
      const inspThickness = new Decimal(
        thickness.convertTo(GeckoMeasurementUnit.Millimeters).num
      )

      // If our custom corrosion rate is a percentage, calculate the value in in/yr
      const newLossBinPerYear = new Decimal(region.corrosionRate)
        .div(region.corrosionRateUnit === '%' ? 100 : 1)
        .mul(region.corrosionRateUnit === '%' ? lossBinPerYear : 1)

      // New thickness after the duration of the liner
      const postArrestThickness = inspThickness.minus(
        newLossBinPerYear.mul(duration)
      )

      // New RCA after the duration of the liner
      const postArrestRca = postArrestThickness.minus(inspThickness.minus(rca))

      const numerator = newLossBinPerYear.mul(duration).add(postArrestRca)
      const denominator = postArrestRca.add(lossBinPerYear.mul(duration))

      // If post-arrest thickness > Tmin, set the corrosion multiplier to the combined corrosion rate
      if (postArrestThickness.gt(inspThickness.minus(rca))) {
        corrosionMultiplier = numerator.div(denominator)
      }
    }

    // If we haven't set the multiplier, it's because we either don't have an RCA/duration,
    // or the post-arrest thickness is less than Tmin, in which case we use the custom corrosion rate
    if (corrosionMultiplier === undefined) {
      if (region.corrosionRateUnit === '%') {
        corrosionMultiplier = new Decimal(region.corrosionRate).div(100)
      } else if (region.corrosionRateUnit === 'in/yr') {
        return {
          shouldReturn: true,
          value: GeckoMeasurement.CreateFromValue(
            region.corrosionRate,
            GeckoMeasurementUnit.Millimeters
          ),
        }
      }
    }
  }

  return { shouldReturn: false, multiplier: corrosionMultiplier }
}
